<template>
  <VModal v-model="modalOpen" :modalTitle="t('exports.modal.title', 'Export Keywords')" size="xs" icon="file_download" :z-index="999999" @opened="fetchDateRange">
    <div class="space-y-6 w-full">
      <!-- Description -->
      <p class="text-gray-600 text-sm">
        {{ t('exports.modal.description', 'Export your keywords to an Excel file with optional date range filtering for comparison.') }} </p>

      <!-- Date Range Picker -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-gray-700"> {{ t('exports.modal.dateRange', 'Date Range for Comparison') }} </label>
        <div class="w-full">
          <VDateRangePicker v-model="dateRange" :minDate="minDate" :maxDate="maxDate" :loading="fetchingDateRange"/>
        </div>
        <p class="text-xs text-gray-500">
          {{ t('exports.modal.dateRangeHint', 'Selecting a date range will enable comparison data in the export.') }} </p>
      </div>

      <!-- Export Button -->
      <div class="flex justify-end">
        <custom-button :isLoading="exporting" @click="exportToExcel" color="primary" size="md" :button-text="t('exports.modal.exportButton', 'Export to Excel')"/>
      </div>
    </div>
  </VModal>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import axios from '@/plugins/axiosInstance'
import { dashboard } from '@/networking/urlManager'
import { saveAs } from 'file-saver'
import { useAssetStore } from '@/stores/asset'
import CustomButton from '@/components/base/buttons/VButton.vue'
import VModal from '@/components/modals/VModal.vue'
import VDateRangePicker from '@/components/base/inputs/VDateRangePicker.vue'

const { t } = useI18n()
const assetStore = useAssetStore()
const exporting = ref(false)
const modalOpen = ref(false)
const fetchingDateRange = ref(false)
const minDate = ref(null)
const maxDate = ref(null)
const dateRange = ref({
  start: null,
  end: null,
})

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  assetList: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'update:exporting'])

watch(() => props.modelValue, (value) => {
  modalOpen.value = value
})

watch(modalOpen, (value) => {
  emit('update:modelValue', value)
})

watch(exporting, (value) => {
  emit('update:exporting', value)
})

/**
 * Geçerli asset için kullanılabilir tarih aralığını getirir
 */
const fetchDateRange = async () => {
  const assetId = getAssetId()
  if (!assetId) return

  fetchingDateRange.value = true

  try {
    const dateRangeData = await fetchDateRangeData(assetId)
    updateDateRangeUI(dateRangeData)
  } catch (error) {
    handleDateRangeError(error)
  } finally {
    fetchingDateRange.value = false
  }
}

/**
 * Aktif asset ID'sini döndürür
 */
const getAssetId = () => {
  return props.assetList.length > 0 ? props.assetList[0] : assetStore.assetId
}

/**
 * API'den tarih aralığı verilerini getirir
 */
const fetchDateRangeData = async (assetId) => {
  const response = await axios.post(dashboard.getExportDateRange, { assetId })
  return response.data && response.data.data ? response.data.data : null
}

/**
 * Tarih aralığı verileriyle UI'ı günceller
 */
const updateDateRangeUI = (dateRangeData) => {
  if (!dateRangeData) return

  const { earliestDate, latestDate } = dateRangeData

  // Tarih seçici için min ve max tarihleri ayarla
  minDate.value = dayjs(earliestDate).toDate()
  maxDate.value = dayjs(latestDate).toDate()

  // Eğer tarih seçilmemişse, varsayılan aralığı ayarla (son 30 gün veya mevcut olanlar)
  if (!dateRange.value.start || !dateRange.value.end) {
    const end = dayjs(latestDate).toDate()
    const startDate = dayjs(latestDate).subtract(30, 'days')
    const start = startDate.isBefore(dayjs(earliestDate))
        ? dayjs(earliestDate).toDate()
        : startDate.toDate()

    dateRange.value = { start, end }
  }
}

/**
 * Tarih aralığını getirme hatalarını yönetir
 */
const handleDateRangeError = (error) => {
  console.error('Failed to fetch date range:', error)
  // Varsayılan tarihleri ayarla
  minDate.value = dayjs().subtract(1, 'year').toDate()
  maxDate.value = dayjs().toDate()
}

const exportToExcel = () => {
  exporting.value = true

  const startDate = dateRange.value.start
      ? dayjs(dateRange.value.start).utc().format()
      : dayjs().subtract(30, 'day').startOf('day').utc().format()

  const endDate = dateRange.value.end
      ? dayjs(dateRange.value.end).utc().format()
      : dayjs().endOf('day').utc().format()

  // Tarih aralığı sağlandığında karşılaştırma verilerini istediğimizi belirtmek için compareMode ekleyin
  const compareMode = !!(dateRange.value.start && dateRange.value.end)

  // Mevcut ise prop'tan asset listesini kullanın, aksi halde asset store kimliğine geri dönün
  const assetListToExport = props.assetList.length > 0
      ? props.assetList
      : [assetStore.assetId]

  const params = {
    assetList: assetListToExport,
    startDate,
    endDate,
    compareMode,
  }

  axios
      .post(dashboard.exportKeywords, params, { responseType: 'arraybuffer' })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] ||
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const fileName = `keyword_analysis_${dayjs().format('YYYY-MM-DD')}.xlsx`
        saveAs(blob, fileName)
        modalOpen.value = false
      })
      .catch((error) => console.error('Export failed:', error))
      .finally(() => {
        exporting.value = false
      })
}

// Bileşen monte edildiğinde tarih aralığını getir (modal zaten açıksa)
onMounted(() => {
  if (modalOpen.value) {
    fetchDateRange()
  }
})
</script>